/**
 * Merchandising CTA GA4 instrumentation.
 *
 * Publishes only `ocs:cta` through window.OCSAnalyticsEvents
 * (assets/ocs-analytics-events.js), the sole owner of the Shopify analytics
 * publish call — see the ARCH-001 guard in tests/analytics. The Custom Pixel
 * owns the dataLayer mapping: nothing here touches dataLayer, and nothing here
 * may, because GTM runs inside the Web Pixel sandbox and the storefront has no
 * dataLayer at all.
 *
 * This replaces two production GTM Custom HTML tags that cannot survive that
 * move:
 *
 *   - tag 60 `HTML - Click listeners - General Components`, which fired on
 *     DOM_READY, cached container elements with getElementsByClassName, and
 *     pushed {event: 'CTA', section, section_heading, index};
 *   - tag 239 `HTML - CTA Title & Position`, which read {{Click Element}} to
 *     resolve a label and pushed {event: 'CTA Title & Position', cta_title_pos}.
 *
 * Both need the storefront DOM, so both are dead in the sandbox. Tag 60 also
 * loaded jQuery over http://, which an HTTPS storefront blocks outright.
 *
 * The pixel collapses that two-hop chain into a single `CTA Title & Position`
 * push carrying all four fields, which is what fires GA4 tag 317. Production
 * relied on the first push's values persisting in dataLayer variables until the
 * second one arrived; sending them together is equivalent and has no window in
 * which a half-populated event can fire.
 *
 * Surfaces opt in from Liquid rather than being listed here:
 *
 *   data-ocs-cta-section          on the section root — unlocalized analytics
 *                                 key, e.g. "MINI TILES"
 *   data-ocs-cta-section-ordinal  on the section root, presence-only — append
 *                                 " - N" for the Nth instance of that section
 *   data-ocs-cta-heading          on the section root — localized heading
 *   data-ocs-cta-title            on the clickable itself — localized label
 *   data-ocs-cta-any              on the section root, presence-only — container
 *                                 mode: any link inside counts, label resolved
 *                                 from the link rather than declared
 *
 * Container mode exists because production tracked six surfaces by attaching to
 * a container and reading whatever was clicked: the store locator, the logo, the
 * mega menu, the hero, the rounded banners and the footer. Those are menus and
 * navigation, rendered from linklists — Liquid cannot label every link there the
 * way a merchandising section labels its tiles. So the label is read at click
 * time instead, by readLabel below.
 *
 * The two modes coexist: a root can carry both, and a link with an explicit
 * data-ocs-cta-title always wins. Only sections that need it opt in.
 *
 * Attributes rather than scraped text, deliberately. Production read
 * `parent.getElementsByTagName('h3')[0].textContent` and
 * `closest('a').innerText`, which throws — silently dropping the whole event —
 * the moment the markup shifts. Horizon's heading is an <h2>, so that scrape
 * would already be wrong here. Liquid has the localized strings in scope at
 * render time and can simply state them.
 *
 * Loaded globally (snippets/scripts.liquid) rather than per-section: production
 * tag 60 fired on the homepage and five landing pages, and any section carrying
 * the attributes above works anywhere without touching this file.
 */
(function () {
  'use strict';

  var EVENT_NAME = 'ocs:cta';

  var ATTR_SECTION = 'data-ocs-cta-section';
  var ATTR_ORDINAL = 'data-ocs-cta-section-ordinal';
  var ATTR_HEADING = 'data-ocs-cta-heading';
  var ATTR_TITLE = 'data-ocs-cta-title';
  var ATTR_ANY = 'data-ocs-cta-any';
  var ATTR_SCOPE = 'data-ocs-cta-scope';

  var SELECTOR_SECTION = '[' + ATTR_SECTION + ']';
  var SELECTOR_TITLE = '[' + ATTR_TITLE + ']';
  var SELECTOR_SCOPE = '[' + ATTR_SCOPE + ']';
  var SELECTOR_ANCHOR = 'a';
  var SELECTOR_IMAGE = 'img';

  /** Production formatted this as `'MINI TILES - ' + (indexP + 1)`. */
  var ORDINAL_SEPARATOR = ' - ';

  function publish(name, payload) {
    var api = window.OCSAnalyticsEvents;
    if (!api || typeof api.publishOcsAnalyticsEvent !== 'function') return false;
    return api.publishOcsAnalyticsEvent(name, payload);
  }

  /* ------------------------------------------------------------------ *
   * Pure-ish helpers. Everything worth asserting lives here.
   * ------------------------------------------------------------------ */

  /**
   * Always the same six keys, empty where unresolved.
   *
   * The empties are load-bearing, not tidiness: GTM dataLayer variables retain
   * their last value across pushes, so omitting `section_heading` would leave
   * {{DLV - section heading}} resolving to whatever the previous CTA or search
   * flyout click left behind, and GA4 would record it against this one. The
   * pixel subscribes with preserveEmpties so these survive compaction.
   *
   * `age_verified` is the exception to the empty-string convention: it is a
   * boolean everywhere it is read, and the pixel's shim tests it with `=== true`,
   * so an unresolved value is false rather than ''.
   *
   * @param {object} partial
   * @returns {object}
   */
  function buildPayload(partial) {
    var given = partial || {};
    function take(key) {
      var value = given[key];
      if (value === undefined || value === null) return '';
      return value;
    }
    return {
      section: take('section'),
      section_heading: take('section_heading'),
      index: take('index'),
      cta_title_pos: take('cta_title_pos'),
      locale: take('locale'),
      age_verified: given.age_verified === true,
    };
  }

  /**
   * `locale` and `age_verified`, which GA4 tag 115 (`CTA Title & Position`)
   * reads alongside the four keys above.
   *
   * Both come from Custom JS variables that scrape the storefront — `locale`
   * queries `.tool-bar … [data-locale]` and `age_verified` calls
   * `Cookies.get('age_verified')`. Sandboxed GTM can see neither, so the values
   * travel on this payload and the pixel reflects them into the sandbox for
   * those variables to find. Nothing in the container changes.
   *
   * Note the pixel does not add either key to the `CTA Title & Position` push
   * itself — no dataLayer variable reads them, so it would achieve nothing. It
   * mirrors them into the sandbox DOM and cookie instead, which is the only
   * place those two Custom JS variables look.
   *
   * Delegated to assets/ocs-search-analytics.js rather than reimplemented: it
   * owns both readers (its own flyout payload carries the same two keys), and
   * snippets/scripts.liquid loads it on the same pages as this file, eight lines
   * earlier. Should it somehow not be there, '' and false are the honest
   * answers, and they leave tag 115 exactly where it stands today rather than
   * costing the CTA event.
   *
   * Read per click rather than cached at load, because the age gate writes its
   * cookie without navigating: a value read at load would report every visitor
   * who verified during the session as unverified.
   *
   * @returns {{locale: string, age_verified: boolean}}
   */
  function readAmbient() {
    var api = window.OCSSearchAnalytics;
    var hasLocale = api && typeof api.readLocale === 'function';
    var hasAgeVerified = api && typeof api.readAgeVerified === 'function';

    return {
      locale: hasLocale ? api.readLocale(document) : '',
      age_verified: hasAgeVerified ? api.readAgeVerified(document) === true : false,
    };
  }

  function attribute(element, name) {
    if (!element || typeof element.getAttribute !== 'function') return null;
    return element.getAttribute(name);
  }

  /**
   * Presence test that does not rely on hasAttribute: a bare HTML attribute
   * reads back as '' rather than null, and '' is the value Liquid emits.
   *
   * @param {Element} root
   * @returns {boolean}
   */
  function wantsOrdinal(root) {
    return attribute(root, ATTR_ORDINAL) !== null;
  }

  /**
   * @param {Element} root
   * @returns {boolean}
   */
  function wantsContainer(root) {
    return attribute(root, ATTR_ANY) !== null;
  }

  /**
   * A link that goes somewhere.
   *
   * Container mode counts and reports these and nothing else, which is a
   * deliberate narrowing of production: tags 60/321/332 fired on *any* click
   * inside the container, so a click on a footer's padding pushed a CTA event
   * with an empty label. Requiring an href covers every click that actually
   * navigates — image links included — without inflating CTA counts with dead
   * space. Widening back is deleting this predicate.
   *
   * @param {Element} node
   * @returns {boolean}
   */
  function isTrackableLink(node) {
    var href;

    if (!node || node.tagName !== 'A') return false;

    href = attribute(node, 'href');
    return typeof href === 'string' && href !== '';
  }

  /**
   * @param {*} value
   * @returns {string} whitespace-collapsed and trimmed, '' for a non-string.
   */
  function collapse(value) {
    if (typeof value !== 'string') return '';
    return value.replace(/\s+/g, ' ').trim();
  }

  /**
   * @param {Element} link
   * @returns {string} the first non-empty alt among the link's images.
   */
  function labelFromImage(link) {
    var images;
    var alt;
    var i;

    if (!link || typeof link.querySelectorAll !== 'function') return '';

    images = link.querySelectorAll(SELECTOR_IMAGE);
    if (!images) return '';

    for (i = 0; i < images.length; i += 1) {
      alt = collapse(attribute(images[i], 'alt'));
      if (alt) return alt;
    }

    return '';
  }

  /**
   * Last path segment, dashes to spaces, uppercased: `/collections/dried-flower`
   * becomes `DRIED FLOWER`.
   *
   * Production's macro 78 did `url.replace('https://ocs.ca/collections/', '')`
   * on the whole href, so it produced the right label for a collection link and
   * garbage for everything else — an icon-only link to /pages/find-a-store
   * reported `HTTPS://OCS.CA/PAGES/FIND A STORE`, and the logo reported
   * `HTTPS://OCS.CA/`. This agrees with production on the case production
   * handled and declines to reproduce the rest.
   *
   * @param {string} href
   * @returns {string}
   */
  function labelFromHref(href) {
    var path = typeof href === 'string' ? href : '';
    var segments;
    var i;

    path = path.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]*/i, '');
    path = path.split('#')[0].split('?')[0];
    segments = path.split('/');

    for (i = segments.length - 1; i >= 0; i -= 1) {
      if (segments[i]) return segments[i].replace(/-/g, ' ').toUpperCase();
    }

    return '';
  }

  /**
   * Container mode's label, in production's order of preference: the text you
   * can see, then the image you can see, then the destination.
   *
   * aria-label sits between the image and the href because an icon-only control
   * has to carry one for accessibility, and it is already the localized name of
   * the thing — the mobile store locator is exactly this case.
   *
   * @param {Element} link
   * @returns {string} '' when nothing resolves, which buildPayload keeps as an
   *   explicit empty rather than dropping.
   */
  function readLabel(link) {
    var text;

    if (!link) return '';

    text = collapse(link.textContent);
    if (text) return text;

    text = labelFromImage(link);
    if (text) return text;

    text = collapse(attribute(link, 'aria-label'));
    if (text) return text;

    return labelFromHref(attribute(link, 'href'));
  }

  /**
   * Walks up from the clicked node to the nearest container root, remembering
   * the first link passed on the way.
   *
   * Walking rather than two `closest` calls is what keeps the link inside the
   * root: `target.closest('a')` alone could return an anchor that *wraps* the
   * container, and attribute the click to a link that is not in the section.
   *
   * @param {Element} target
   * @returns {{root: Element, link: Element}|null} null when the click was not
   *   inside a container root, or landed on dead space within one.
   */
  function resolveContainer(target) {
    var node = target;
    var link = null;

    while (node) {
      if (!link && isTrackableLink(node)) link = node;
      if (wantsContainer(node)) return link ? { root: node, link: link } : null;
      node = node.parentElement;
    }

    return null;
  }

  /**
   * Every section root on the page whose `data-ocs-cta-section` equals `value`,
   * in document order.
   *
   * Matched by reading each candidate's attribute rather than by building a
   * `[data-ocs-cta-section="…"]` selector, so a section value containing a
   * quote cannot break the query.
   *
   * @param {string} value
   * @returns {Element[]}
   */
  function rootsMatching(value) {
    var roots = document.querySelectorAll(SELECTOR_SECTION);
    var matched = [];
    var i;

    if (!roots) return matched;

    for (i = 0; i < roots.length; i += 1) {
      if (attribute(roots[i], ATTR_SECTION) === value) matched.push(roots[i]);
    }

    return matched;
  }

  /**
   * `roots` narrowed to the one rendered copy the click happened in.
   *
   * Page-global counting assumes each surface appears once. The mega nav breaks
   * that: blocks/mega-panel.liquid renders a `desktop-cta` and a `mobile`
   * template per panel, ocs-header-mega-panels.js clones both into live slots
   * with no viewport gate, and the two copies carry identical section values —
   * so a page-global count tallies the hidden copy alongside the visible one and
   * the number a click reports depends on which breakpoint produced it.
   *
   * ocs.ca has no equivalent: snippets/mega-menu.liquid renders one tree and
   * hides the featured block on desktop with `only-mobile-tablet`, so there is
   * only ever one instance to count. A scope boundary reproduces that, and it
   * does so without inventing a mobile-specific section value — GA4 keeps seeing
   * one string per surface, and desktop and mobile agree on the index.
   *
   * Deliberately marked in the markup rather than keyed off the mega-panel slot
   * attribute here: any duplicated tree has this problem, and the boundary is a
   * fact about the markup, not about analytics.
   *
   * @param {Array<Element>} roots
   * @param {Element} link the clicked element
   * @returns {Array<Element>} `roots` unchanged when the click is outside any
   *   scope, which is every surface that renders once.
   */
  function withinScope(roots, link) {
    var boundary = link && typeof link.closest === 'function' ? link.closest(SELECTOR_SCOPE) : null;
    var matched = [];
    var i;

    if (!boundary) return roots;

    for (i = 0; i < roots.length; i += 1) {
      if (typeof roots[i].closest !== 'function') continue;
      if (roots[i].closest(SELECTOR_SCOPE) === boundary) matched.push(roots[i]);
    }

    return matched;
  }

  /**
   * 1-based position of `root` among its same-kind siblings — production's
   * `indexP + 1`, without its hardcoded class list.
   *
   * @param {Element} root
   * @param {string} value
   * @returns {number} 0 when `root` is not on the page, which cannot happen for
   *   an element that was just clicked.
   */
  function ordinalFor(root, value) {
    var matched = rootsMatching(value);
    var i;

    for (i = 0; i < matched.length; i += 1) {
      if (matched[i] === root) return i + 1;
    }

    return 0;
  }

  /**
   * @param {Element} root
   * @returns {string} '' when the root carries no section value.
   */
  function readSection(root) {
    var value = attribute(root, ATTR_SECTION);
    var position;

    if (!value) return '';
    if (!wantsOrdinal(root)) return value;

    position = ordinalFor(root, value);
    return position ? value + ORDINAL_SEPARATOR + position : value;
  }

  /**
   * 1-based position of the clicked link among the tracked links of its kind.
   *
   * The counting scope follows the ordinal flag, because the two together have
   * to make a click uniquely identifiable:
   *
   *   - Ordinal section — count within this root alone. The ` - N` already in
   *     `section` says which instance was clicked, so restarting at 1 per
   *     instance is unambiguous. Production did exactly this for mini tiles:
   *     `parent.querySelectorAll('a')`, indexed per parent.
   *   - Plain section — count across every root of the same kind on the page,
   *     in document order. `section` is identical for all of them, so a
   *     per-root count would report `index: 1` for all six TALL BANNERs on
   *     /pages/wellness. Production's tag 239 was page-global here too, via
   *     `2*i + c + 1` and friends, where `i` looped over every container.
   *
   * Only elements carrying `data-ocs-cta-title` are counted, which Liquid puts
   * on real links and not on the unlinked fallback markup. That reproduces
   * production's `querySelectorAll('a')` exactly, including its quirk: an
   * unlinked tile is skipped, so every tile after it reports one position
   * lower than its visual position. Kept rather than fixed, so the numbers stay
   * comparable with historical data.
   *
   * Container mode counts every link that goes somewhere instead, since it has
   * no title attributes to count. The two bases only ever differ inside a
   * section that mixes both, which none currently does.
   *
   * Either mode also counts the root itself when the root *is* the link, which
   * happens wherever there is no wrapper to hang the section attributes on: the
   * store locator and the logo in container mode, and the mega nav's featured
   * CTA row and featured tile in title mode. Missing the title-mode half of
   * that is what made every one of those surfaces report `index: ''` — the
   * descendant scan below cannot find the clicked link, because
   * `querySelectorAll` only sees descendants and the link is the root.
   *
   * @param {Element} root
   * @param {Element} link
   * @param {boolean} [container] count links rather than title attributes.
   * @returns {number|string} 1-based position, or '' when it cannot be resolved.
   */
  function readIndex(root, link, container) {
    var selector = container ? SELECTOR_ANCHOR : SELECTOR_TITLE;
    var value;
    var scope;
    var links;
    var position = 0;
    var counts;
    var r;
    var i;

    if (!root || typeof root.querySelectorAll !== 'function') return '';

    // A blank section value would make every unnamed root one another's sibling,
    // so such a root counts alone. `section` is '' in that case anyway.
    value = attribute(root, ATTR_SECTION);
    scope = wantsOrdinal(root) || !value ? [root] : rootsMatching(value);
    scope = withinScope(scope, link);

    for (r = 0; r < scope.length; r += 1) {
      if (typeof scope[r].querySelectorAll !== 'function') continue;

      counts = container ? isTrackableLink(scope[r]) : attribute(scope[r], ATTR_TITLE) !== null;

      if (counts) {
        position += 1;
        if (scope[r] === link) return position;
      }

      links = scope[r].querySelectorAll(selector);
      if (!links) continue;

      for (i = 0; i < links.length; i += 1) {
        if (container && !isTrackableLink(links[i])) continue;
        position += 1;
        if (links[i] === link) return position;
      }
    }

    return '';
  }

  /* ------------------------------------------------------------------ */

  /**
   * @param {object} event
   * @returns {boolean} whether an event was published.
   */
  function handleClick(event) {
    var target = event && event.target;
    var link;
    var root;
    var container;
    var ambient;

    try {
      if (!target || typeof target.closest !== 'function') return false;

      // A declared title wins wherever it is found, so a container-mode section
      // can still label individual links explicitly. Nothing here checks for an
      // <a>: the unlinked tile markup simply carries no title attribute.
      link = target.closest(SELECTOR_TITLE);
      root = link && typeof link.closest === 'function' ? link.closest(SELECTOR_SECTION) : null;

      if (root) {
        ambient = readAmbient();
        return publish(
          EVENT_NAME,
          buildPayload({
            section: readSection(root),
            section_heading: attribute(root, ATTR_HEADING),
            index: readIndex(root, link),
            cta_title_pos: attribute(link, ATTR_TITLE),
            locale: ambient.locale,
            age_verified: ambient.age_verified,
          })
        );
      }

      container = resolveContainer(target);
      if (!container) return false;

      ambient = readAmbient();
      return publish(
        EVENT_NAME,
        buildPayload({
          section: readSection(container.root),
          section_heading: attribute(container.root, ATTR_HEADING),
          index: readIndex(container.root, container.link, true),
          cta_title_pos: readLabel(container.link),
          locale: ambient.locale,
          age_verified: ambient.age_verified,
        })
      );
    } catch (error) {
      /* Analytics must never interrupt a navigation. */
      return false;
    }
  }

  /**
   * Capture phase, so a section that stops propagation on its own click
   * handling cannot swallow the event before it is recorded.
   *
   * @param {Document|Element} root
   */
  function init(root) {
    if (!root || typeof root.addEventListener !== 'function') return;
    root.addEventListener('click', handleClick, true);
  }

  window.OCSCtaAnalytics = {
    ATTRIBUTES: {
      any: ATTR_ANY,
      section: ATTR_SECTION,
      ordinal: ATTR_ORDINAL,
      heading: ATTR_HEADING,
      title: ATTR_TITLE,
      scope: ATTR_SCOPE,
    },
    buildPayload: buildPayload,
    handleClick: handleClick,
    init: init,
    labelFromHref: labelFromHref,
    ordinalFor: ordinalFor,
    readAmbient: readAmbient,
    readIndex: readIndex,
    readLabel: readLabel,
    readSection: readSection,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function bootstrap() {
      init(document);
    });
  } else {
    init(document);
  }
})();
